import {
  ExclamationCircleOutlineIcon,
  Icon,
  type IconComponent,
} from '@fountain/ripple/icon';
import { Tooltip } from '@fountain/ripple/tooltip';
import { Typography } from '@material-ui/core';
import React, { useId } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import {
  APPROVER_PRESENTATION,
  ApproverState,
  approverState,
  ChainApprover,
  LEVEL_PRESENTATION,
  LevelState,
  levelProgress,
  visibleApprovers,
} from 'components/ApprovalLevelChain';
import chainMessages from 'components/ApprovalLevelChain/messages';

import ruleMessages from '../messages';
import { ApprovalRound, approvalRounds, RoundMember } from './approvalRounds';
import messages from './messages';
import { useStyles } from './styles';
import { RuleGroup } from './types';

interface ApprovalStatusCellProps {
  rules: RuleGroup[];
  /**
   * Whether the request itself has finished.
   *
   * Required by `levelState` rather than derivable from the levels: a cancelled
   * request leaves its open level reading `active`, so a cell that judged each
   * level on its own would say a withdrawn approval was still awaiting somebody.
   */
  requestSettled: boolean;
}

type Mark = 'approved' | 'rejected' | 'pending' | 'blocked' | 'neutral';

const LEVEL_MARK: Record<LevelState, Mark> = {
  approved: 'approved',
  rejected: 'rejected',
  active: 'pending',
  notStarted: 'neutral',
  notTriggered: 'neutral',
  cancelled: 'neutral',
};

const APPROVER_MARK: Record<ApproverState, Mark> = {
  approved: 'approved',
  rejected: 'rejected',
  pending: 'pending',
  cancelled: 'neutral',
};

/**
 * One glyph, tinted for its state. Named when no word beside it says the
 * state, which in this cell is every level line and every approver.
 */
const Glyph: React.FC<{
  icon: IconComponent;
  mark: Mark;
  label?: string;
}> = ({ icon, mark, label }) => {
  const styles = useStyles();
  const markClass = {
    approved: styles.markApproved,
    rejected: styles.markRejected,
    pending: styles.markPending,
    blocked: styles.markRejected,
    neutral: styles.markNeutral,
  }[mark];

  return (
    <Icon
      aria-label={label}
      className={markClass}
      color="inherit"
      icon={icon}
      role={label ? 'img' : undefined}
      size="sm"
    />
  );
};

/**
 * The next step for something that blocks the request, on hover and on focus.
 *
 * The trigger takes focus and nothing else. It is no control: whoever can fix
 * the rule does it on the Approver rules tab. A screen reader hears the step as
 * the trigger's description, because the tooltip itself is not announced.
 */
const NextStep: React.FC<
  React.PropsWithChildren<{ step: MessageDescriptor }>
> = ({ step, children }) => {
  const styles = useStyles();
  const intl = useIntl();
  const descriptionId = useId();
  const text = intl.formatMessage(step);

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span
            aria-describedby={descriptionId}
            className={styles.tooltipTrigger}
            tabIndex={0}
          />
        }
      >
        {children}
      </Tooltip.Trigger>
      <span hidden id={descriptionId}>
        {text}
      </span>
      <Tooltip.Content align="start" side="bottom" size="md">
        {text}
      </Tooltip.Content>
    </Tooltip>
  );
};

const approverName = (approver: ChainApprover, unnamed: string) =>
  approver.name ?? unnamed;

// The server sets the reason only on an assignment still waiting, and this
// keeps a closed level's row from claiming otherwise.
const blockedReasonOf = (approver: ChainApprover, state: ApproverState) =>
  state === 'pending' ? approver.blockedReason : null;

const ApproverLine: React.FC<{
  approver: ChainApprover;
  state: ApproverState;
}> = ({ approver, state }) => {
  const styles = useStyles();
  const intl = useIntl();
  const name = approverName(
    approver,
    intl.formatMessage(chainMessages.unnamedApprover),
  );
  const blocked = blockedReasonOf(approver, state);

  if (!blocked) {
    const { Icon: StateIcon, label } = APPROVER_PRESENTATION[state];
    return (
      <div className={styles.approverLine}>
        <Glyph
          icon={StateIcon}
          label={intl.formatMessage(label)}
          mark={APPROVER_MARK[state]}
        />
        <p className={styles.approverName}>{name}</p>
      </div>
    );
  }

  const left = blocked === 'removed';
  return (
    <div>
      <div className={styles.approverLine}>
        <Glyph
          icon={ExclamationCircleOutlineIcon}
          label={intl.formatMessage(chainMessages.approverCannotApprove)}
          mark="blocked"
        />
        <NextStep
          step={
            left
              ? messages.approverLeftAccountNextStep
              : messages.approverLostPermissionNextStep
          }
        >
          <span className={styles.approverName}>{name}</span>
        </NextStep>
      </div>
      <p className={styles.blockedReason}>
        <FormattedMessage
          {...(left
            ? messages.approverLeftAccount
            : messages.approverLostPermission)}
        />
      </p>
    </div>
  );
};

const NoApproverLine: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.approverLine}>
      <Glyph icon={ExclamationCircleOutlineIcon} mark="blocked" />
      <NextStep step={messages.noApproverNextStep}>
        <span className={styles.noApprover}>
          <FormattedMessage {...messages.noApprover} />
        </span>
      </NextStep>
    </div>
  );
};

/**
 * The approvers one level of a round shows, each read against its own level's
 * state rather than the round's.
 *
 * In a round the request waits on, a level that is not itself waiting waits on
 * nobody: another rule's level may already be approved, or not reached yet.
 * Its unanswered approvers keep a pending assignment, and a departed one keeps
 * its `blocked_reason`, so listing them would name people nobody waits on. Only
 * the ones who answered stay.
 */
const memberApprovers = (member: RoundMember, roundState: LevelState) =>
  visibleApprovers(member.level)
    .filter(
      approver =>
        roundState !== 'active' ||
        member.state === 'active' ||
        approver.status !== 'pending',
    )
    .map(approver => ({
      approver,
      state: approverState(approver, member.state),
    }));

// Only a level the request waits on can lack the approver it waits for.
const waitsOnNobody = (member: RoundMember) =>
  member.state === 'active' && member.level.needsAttention;

/** Who the waiting level is waiting on, under its line. */
const RoundApprovers: React.FC<{ round: ApprovalRound }> = ({ round }) => {
  const styles = useStyles();

  return (
    <div className={styles.roundApprovers}>
      {round.members.map((member, memberIndex) => (
        // Index in the key as well as the step id: a level the server carried
        // from an earlier approval has no step on this request, so every such
        // level shares step id 0.
        <React.Fragment key={`${member.level.stepId}-${memberIndex}`}>
          {waitsOnNobody(member) && <NoApproverLine />}
          {memberApprovers(member, round.state).map(
            ({ approver, state }, index) => (
              <ApproverLine
                approver={approver}
                key={`${approver.id ?? 'unknown'}-${index}`}
                state={state}
              />
            ),
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * "N of M approved", only where it is honest: a live `all` level that needs two
 * or more sign-offs and has not escalated. A round that holds levels from
 * several rules is no one level to count toward, so it prints none.
 */
const roundProgress = (round: ApprovalRound, requestSettled: boolean) => {
  if (round.members.length !== 1) return null;
  const [{ level }] = round.members;
  if (level.escalated) return null;
  return levelProgress(level, requestSettled);
};

/** The card a level line opens: its state, then each approver's. */
const LevelCard: React.FC<{
  round: ApprovalRound;
  progress: { approved: number; total: number } | null;
}> = ({ round, progress }) => {
  const styles = useStyles();
  const intl = useIntl();
  const { Icon: StateIcon, label } = LEVEL_PRESENTATION[round.state];
  const rows = round.members.flatMap(member =>
    memberApprovers(member, round.state),
  );
  const unnamed = intl.formatMessage(chainMessages.unnamedApprover);
  const noApprover = round.members.some(waitsOnNobody);

  return (
    <>
      <div className={styles.cardHeader}>
        <Glyph icon={StateIcon} mark={LEVEL_MARK[round.state]} />
        <span className={styles.cardState}>
          <FormattedMessage {...label} />
        </span>
      </div>
      {progress && (
        <p className={styles.cardProgress}>
          <FormattedMessage
            {...chainMessages.levelProgress}
            values={progress}
          />
        </p>
      )}
      {noApprover && (
        <p className={styles.cardNote}>
          <FormattedMessage {...messages.noApproverCardNote} />
        </p>
      )}
      {rows.length > 0 && (
        <div className={styles.cardApprovers}>
          {rows.map(({ approver, state }, index) => {
            const blocked = blockedReasonOf(approver, state);
            const presentation = APPROVER_PRESENTATION[state];
            return (
              <div
                className={styles.cardApprover}
                key={`${approver.id ?? 'unknown'}-${index}`}
              >
                <div className={styles.cardApproverLine}>
                  <Glyph
                    icon={
                      blocked ? ExclamationCircleOutlineIcon : presentation.Icon
                    }
                    mark={blocked ? 'blocked' : APPROVER_MARK[state]}
                  />
                  <span className={styles.cardApproverName}>
                    {approverName(approver, unnamed)}
                  </span>
                  {/* The chain's word for this case, in place of Pending: a
                      blocked approver must never read as merely pending. */}
                  <span className={styles.cardApproverState}>
                    <FormattedMessage
                      {...(blocked
                        ? chainMessages.approverCannotApprove
                        : presentation.label)}
                    />
                  </span>
                </div>
                {blocked && (
                  <p className={styles.cardBlockedReason}>
                    <FormattedMessage
                      {...(blocked === 'removed'
                        ? messages.approverLeftAccount
                        : messages.approverLostPermission)}
                    />
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const RoundLine: React.FC<{
  round: ApprovalRound;
  requestSettled: boolean;
}> = ({ round, requestSettled }) => {
  const styles = useStyles();
  const intl = useIntl();
  const progressId = useId();
  const { Icon: StateIcon, label } = LEVEL_PRESENTATION[round.state];
  const progress = roundProgress(round, requestSettled);
  const logic = round.approverLogic
    ? intl.formatMessage(
        round.approverLogic === 'all'
          ? ruleMessages.approverLogicAll
          : ruleMessages.approverLogicAny,
      )
    : null;

  let text: string;
  if (logic) {
    text = round.name
      ? intl.formatMessage(messages.roundLabelNamed, {
          number: round.position,
          name: round.name,
          logic,
        })
      : intl.formatMessage(messages.roundLabel, {
          number: round.position,
          logic,
        });
  } else {
    // The round's levels disagree on Any/All, so neither word names it.
    text = round.name
      ? intl.formatMessage(ruleMessages.levelRowNamed, {
          number: round.position,
          name: round.name,
        })
      : intl.formatMessage(ruleMessages.levelRow, { number: round.position });
  }

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <div
            aria-describedby={progress ? progressId : undefined}
            className={styles.roundLine}
            tabIndex={0}
          />
        }
      >
        <Glyph
          icon={StateIcon}
          label={intl.formatMessage(label)}
          mark={LEVEL_MARK[round.state]}
        />
        <span className={styles.roundLabel}>{text}</span>
      </Tooltip.Trigger>
      {progress && (
        <span hidden id={progressId}>
          {intl.formatMessage(chainMessages.levelProgress, progress)}
        </span>
      )}
      <Tooltip.Content
        align="start"
        className={styles.levelCard}
        color="light"
        showArrow={false}
        side="bottom"
        sideOffset={4}
      >
        <LevelCard progress={progress} round={round} />
      </Tooltip.Content>
    </Tooltip>
  );
};

/**
 * How far one approval has got, and who it waits on — the queue's Approval
 * status column.
 *
 * One line per round: the levels asked at one moment, which several rules can
 * contribute to (`approvalRounds`). The line is the round's label alone; its
 * glyph carries the state, and its card says it in words on hover and on
 * focus. Under the round a request waits on, each approver is named with their
 * own glyph, and one who cannot act says why, with the next step on hover and
 * on focus.
 *
 * State comes from `levelState`, and words and glyphs from
 * `LEVEL_PRESENTATION` and `APPROVER_PRESENTATION`, all shared with the review
 * panel's rail. The cell holds no control: the fix for a blocked approver is a
 * rule change, made on the Approver rules tab.
 */
export const ApprovalStatusCell: React.FC<ApprovalStatusCellProps> = ({
  rules,
  requestSettled,
}) => {
  const styles = useStyles();
  const rounds = approvalRounds(rules, requestSettled);

  if (rounds.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        <FormattedMessage {...messages.noLevels} />
      </Typography>
    );
  }

  return (
    <div className={styles.statusCell} data-testid="approval-status">
      {rounds.map(round => (
        <React.Fragment key={round.position}>
          <RoundLine requestSettled={requestSettled} round={round} />
          {round.state === 'active' && <RoundApprovers round={round} />}
        </React.Fragment>
      ))}
    </div>
  );
};
